import * as THREE from 'three';
import { CHANNEL_PALETTE } from '../constants.js';
import dreamVert from '../shaders/dream.vert';
import dreamFrag from '../shaders/dream.frag';

/**
 * Dream Mode — dissolves the knowledge graph into a flowing particle symphony.
 *
 * Every block becomes a luminous particle, carried by multi-octave curl noise
 * through rivers of light. Channel colors bleed into aurora ribbons.
 * Dust particles fill the negative space, breathing with the data.
 *
 * The data IS the art. The organization IS the palette.
 * The structure IS the composition.
 */

const DUST_COUNT = 4000;

export class DreamMode {
  constructor(sceneManager, graphData, nodeRenderer, edgeRenderer, cameraController, postProcessing) {
    this.scene = sceneManager.scene;
    this.graphData = graphData;
    this.nodeRenderer = nodeRenderer;
    this.edgeRenderer = edgeRenderer;
    this.cameraController = cameraController;
    this.postProcessing = postProcessing;

    this.active = false;
    this._points = null;
    this._material = null;
    this._progress = 0;
    this._elapsed = 0;
    this._originalBloom = 0;
    this._built = false;

    // Button handler
    const btn = document.getElementById('btn-dream');
    if (btn) {
      btn.addEventListener('click', () => {
        if (this.active) this.deactivate();
        else this.activate();
      });
    }
  }

  _build() {
    if (this._built) return;
    this._built = true;

    const blocks = this.graphData.blocks;
    const totalCount = blocks.length + DUST_COUNT;

    const origins = new Float32Array(totalCount * 3);
    const colors = new Float32Array(totalCount * 3);
    const phases = new Float32Array(totalCount);
    const sizes = new Float32Array(totalCount);
    const speeds = new Float32Array(totalCount);

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    // ─── Primary particles: one per block ───
    for (let i = 0; i < blocks.length; i++) {
      const pos = this.nodeRenderer.getBlockPosition(i);
      const x = pos.x, y = pos.y, z = pos.z;

      origins[i * 3] = x;
      origins[i * 3 + 1] = y;
      origins[i * 3 + 2] = z;

      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
      minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);

      // Channel color
      const blockChannels = this.graphData.blockToChannelsMap[blocks[i].id] || [];
      const chId = blockChannels[0];
      const chIdx = chId ? (this.graphData.channelIndexMap[chId] || 0) : 0;
      const c = new THREE.Color(CHANNEL_PALETTE[chIdx % CHANNEL_PALETTE.length]);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;

      phases[i] = Math.random();
      sizes[i] = 3.0 + Math.random() * 5.0;
      speeds[i] = 0.5 + Math.random() * 0.8;
    }

    // ─── Dust particles: ambient cosmic matter ───
    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;
    const rangeZ = maxZ - minZ || 1;
    const pad = 0.4;

    for (let i = 0; i < DUST_COUNT; i++) {
      const idx = blocks.length + i;
      origins[idx * 3] = minX - rangeX * pad + Math.random() * rangeX * (1 + 2 * pad);
      origins[idx * 3 + 1] = minY - rangeY * pad + Math.random() * rangeY * (1 + 2 * pad);
      origins[idx * 3 + 2] = minZ - rangeZ * pad + Math.random() * rangeZ * (1 + 2 * pad);

      const c = new THREE.Color(CHANNEL_PALETTE[Math.floor(Math.random() * CHANNEL_PALETTE.length)]);
      // Desaturate dust slightly
      c.lerp(new THREE.Color(0.3, 0.3, 0.4), 0.3);
      colors[idx * 3] = c.r;
      colors[idx * 3 + 1] = c.g;
      colors[idx * 3 + 2] = c.b;

      phases[idx] = Math.random();
      sizes[idx] = 1.0 + Math.random() * 2.5;
      speeds[idx] = 0.7 + Math.random() * 1.3;
    }

    // ─── Geometry ───
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(origins), 3));
    geometry.setAttribute('aOrigin', new THREE.BufferAttribute(origins, 3));
    geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));
    geometry.setAttribute('aBaseSize', new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute('aSpeed', new THREE.BufferAttribute(speeds, 1));

    // ─── Material ───
    // Aurora wave uniforms: 5 waves, each with direction(3) + speed(1) + width(1) + offset(1) + hue(3)
    const waveDirections = new Float32Array(5 * 3);
    const waveSpeeds = new Float32Array(5);
    const waveWidths = new Float32Array(5);
    const waveOffsets = new Float32Array(5);
    const waveHues = new Float32Array(5 * 3);

    this._material = new THREE.ShaderMaterial({
      vertexShader: dreamVert,
      fragmentShader: dreamFrag,
      uniforms: {
        uTime: { value: 0 },
        uProgress: { value: 0 },
        uAurora: { value: 0.0 },
        uWaveDirections: { value: waveDirections },
        uWaveSpeeds: { value: waveSpeeds },
        uWaveWidths: { value: waveWidths },
        uWaveOffsets: { value: waveOffsets },
        uWaveHues: { value: waveHues },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this._points = new THREE.Points(geometry, this._material);
    this._points.frustumCulled = false;
    this._points.visible = false;
    this.scene.add(this._points);
  }

  activate() {
    if (this.active) return;
    this.active = true;
    this._progress = 0;
    this._elapsed = 0;

    this._build();

    // Hide the graph
    this.nodeRenderer.mesh.visible = false;
    for (const cm of this.nodeRenderer.channelMeshes) cm.visible = false;
    this.edgeRenderer.setVisible(false);

    // Show particles
    this._points.visible = true;

    // Boost bloom + enable comet trails
    if (this.postProcessing) {
      this._originalBloom = this.postProcessing.bloomPass.strength;
      this.postProcessing.bloomPass.strength = 1.2;
      this.postProcessing.afterimagePass.enabled = true;
    }

    document.getElementById('btn-dream').classList.add('active');
  }

  deactivate() {
    if (!this.active) return;
    this.active = false;

    // Show the graph
    this.nodeRenderer.mesh.visible = true;
    for (const cm of this.nodeRenderer.channelMeshes) cm.visible = true;
    this.edgeRenderer.setVisible(true);

    // Hide particles
    if (this._points) this._points.visible = false;

    // Restore bloom + disable comet trails
    if (this.postProcessing) {
      this.postProcessing.bloomPass.strength = this._originalBloom;
      this.postProcessing.afterimagePass.enabled = false;
    }

    document.getElementById('btn-dream').classList.remove('active');
  }

  /**
   * Sync aurora wave parameters into dream shader uniforms.
   * Called externally when aurora mode is active alongside dream mode.
   */
  setAuroraWaves(waves) {
    if (!this._material) return;
    const u = this._material.uniforms;

    for (let i = 0; i < waves.length && i < 5; i++) {
      const w = waves[i];
      u.uWaveDirections.value[i * 3] = w.dx;
      u.uWaveDirections.value[i * 3 + 1] = w.dy;
      u.uWaveDirections.value[i * 3 + 2] = w.dz;
      u.uWaveSpeeds.value[i] = w.speed;
      u.uWaveWidths.value[i] = w.width;
      u.uWaveOffsets.value[i] = w.offset;
      u.uWaveHues.value[i * 3] = w.hue[0];
      u.uWaveHues.value[i * 3 + 1] = w.hue[1];
      u.uWaveHues.value[i * 3 + 2] = w.hue[2];
    }
  }

  setAuroraActive(active) {
    if (!this._material) return;
    this._material.uniforms.uAurora.value = active ? 1.0 : 0.0;
  }

  update(delta) {
    if (!this.active || !this._material) return;

    // Smooth ease-in over ~3 seconds
    this._progress = Math.min(1.0, this._progress + delta * 0.35);
    // Smooth curve for the transition
    const easedProgress = this._progress * this._progress * (3 - 2 * this._progress);

    this._elapsed += delta;

    this._material.uniforms.uTime.value = this._elapsed;
    this._material.uniforms.uProgress.value = easedProgress;

    // Gentle rotation of the whole particle cloud
    if (this._points) {
      this._points.rotation.y += delta * 0.015;
    }
  }
}
